'use client';

import { useState } from 'react';
import { GroupedMagazine, type GroupedMagazineItem } from '@pixum/grouped-magazine';
import ThemeProvider from '@pixum/theme-provider';
import { TEXT_VARIANT } from '@pixum/text';
import { Image, IMAGE_LOADING } from '../../image';
import { Video } from '../../video/Video';
import { RichTextBody, isRichTextEmpty } from '../../richtText';
import { MagazineActions } from './partials';
import { getMagazineVariant } from '../utils/getMagazineVariant';
import { getMediaAspectRatioStyle, getVideoAspectRatioStyle } from '../utils/getMediaAspectRatioStyle';
import '../mediaWrapper.css';
import type { Device, GroupedMagazineNode, GroupedMagazineItemNode } from 'data-layer/types';

interface GroupedMagazineComponentProps {
  groupedMagazine: GroupedMagazineNode;
  device: Device;
  groupIndex: number;
  className?: string;
}

/**
 * Transforms a single `GroupedMagazineItemNode` into a `GroupedMagazineItem` view model.
 *
 * @param item - Raw item node from the data layer
 * @param device - Current device type, used for image sizing
 * @param constants - Optional i18n constants forwarded to `MagazineActions`
 */
function transformItem(
  item: GroupedMagazineItemNode,
  device: Device,
  constants?: Record<string, string>
): GroupedMagazineItem {
  const text =
    item.text && !isRichTextEmpty(item.text) ? (
      <RichTextBody text={item.text} variant={TEXT_VARIANT.BODY} />
    ) : undefined;
  const actions =
    item.links.length > 0 ? (
      <MagazineActions buttons={item.links} constants={constants || {}} device={device} />
    ) : undefined;
  const media = item.video ? (
    <div className="magazine-media-wrapper magazine-media-wrapper--video" style={getVideoAspectRatioStyle()}>
      <Video video={item.video} />
    </div>
  ) : item.image ? (
    <div className="magazine-media-wrapper" style={getMediaAspectRatioStyle(item.image)}>
      <Image image={item.image} loading={IMAGE_LOADING.LAZY} device={device} />
    </div>
  ) : undefined;

  return {
    id: item.id,
    subheadline: item.subheadline,
    media,
    text,
    actions
  };
}

/**
 * Maps `GroupedMagazineItemNode` data models to `GroupedMagazineItem` view models consumed by the
 * `@pixum/grouped-magazine` component. Resolves media (video or image), rich text body, and
 * action buttons for each item.
 *
 * @param items - Raw item nodes from the data layer
 * @param device - Current device type, used for image sizing
 * @param constants - Optional i18n constants forwarded to `MagazineActions`
 */
function transformItems(
  items: GroupedMagazineItemNode[],
  device: Device,
  constants?: Record<string, string>
): GroupedMagazineItem[] {
  return items.map(item => transformItem(item, device, constants));
}

export function GroupedMagazineComponent(props: GroupedMagazineComponentProps) {
  const { groupedMagazine, device, groupIndex } = props;
  const [activeIndex, setActiveIndex] = useState(0);

  const items = transformItems(groupedMagazine.items, device, groupedMagazine.constants);
  const chips =
    groupedMagazine.chips && groupedMagazine.chips.length > 0 ? { chips: groupedMagazine.chips } : undefined;

  const groupedMagazineProps = {
    id: groupedMagazine.id,
    headline: groupedMagazine.headline,
    overline: groupedMagazine.overline,
    description: groupedMagazine.description,
    items,
    chips,
    variant: groupedMagazine.variant,
    contentVariant: getMagazineVariant(groupIndex),
    focus: groupedMagazine.focus,
    activeIndex,
    onActiveIndexChange: setActiveIndex
  };

  return (
    <ThemeProvider theme={groupedMagazine.items[activeIndex]?.theme}>
      <GroupedMagazine {...groupedMagazineProps} className={props.className} />
    </ThemeProvider>
  );
}
